import React, { useCallback, useMemo, useRef } from 'react';

import { JwtAuthProvider } from '@src/corelib/auth/providers';
import { useJwtStorage } from '@src/corelib/auth/storages';

const AuthenticationContext = React.createContext(null);

function createAuthProvider(params) {
  return new JwtAuthProvider(params);
}

function AuthenticationProvider({ children }) {
  const storage = useJwtStorage();
  const provider = useRef(createAuthProvider({ storage, credentials: storage.credentials }));

  const login = useCallback((username, password) => provider.current.login(username, password), []);
  const logout = useCallback(() => provider.current.logout(), []);
  const verifyToken = useCallback(() => provider.current.verifyToken(), []);
  const refreshToken = useCallback((loginAsToken) => provider.current.refreshToken(loginAsToken), []);
  const authenticate = useCallback((data) => provider.current.authenticate(data), []);
  const unauthenticate = useCallback(() => provider.current.unauthenticate(), []);

  const contextValue = useMemo(() => ({
    login,
    logout,
    verifyToken,
    refreshToken,
    authenticate,
    unauthenticate,
    credentials: storage.credentials,
    isAuthenticated: storage.isAuthenticated,
    user: storage.credentials?.user,
  }), [
    authenticate,
    login,
    logout,
    refreshToken,
    storage.credentials,
    storage.isAuthenticated,
    unauthenticate,
    verifyToken,
  ]);

  return (
    <AuthenticationContext.Provider value={contextValue}>
      {children}
    </AuthenticationContext.Provider>
  );
}

AuthenticationProvider.displayName = 'AuthenticationProvider';
export { AuthenticationContext, AuthenticationProvider };
